import { Component, EventEmitter, Input, Output, signal, model } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonComponent, FieldLabelComponent, TextareaComponent } from 'nebula-ui-kit';

export interface ConfirmReasonData {
  title: string;
  description?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  textareaPlaceholder?: string;
  textareaLabel?: string;
  type?: 'default' | 'reject';
}

export interface ConfirmReasonResult {
  confirmed: boolean;
  reason: string;
}

@Component({
  selector: 'app-confirm-reason-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TextareaComponent, ButtonComponent, FieldLabelComponent],
  template: `
    <div
      class="modal-overlay"
      (click)="onOverlayClick($event)"
      tabindex="0"
      role="button"
      aria-label="Close modal"
    >
      <div class="modal-container">
        <div
          class="
            dialog-header dialog-header-primary
          "
        >
          <h3 class="dialog-title">{{ data.title }}</h3>
          <button
            type="button"
            class="close-btn"
            (click)="onCancel()"
            [disabled]="loading()"
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        <div class="dialog-content">
          @if (data.description) {
            <p class="modal-description">{{ data.description }}</p>
          }

          <div class="textarea-wrapper">
            <lib-field-label
              [label]="data.textareaLabel || ''"
              [required]="data.required !== false"
            />
            <lib-textarea
              [(value)]="reason"
              [placeholder]="data.textareaPlaceholder || 'Por favor, indique el motivo...'"
              [rows]="4"
              [maxLength]="data.maxLength || 300"
              [showCharCount]="true"
              [required]="data.required !== false"
              [errorMsg]="getErrorMessage()"
              (blur)="onBlur()"
              (input)="onInput()"
            />
          </div>
        </div>

        <div class="dialog-footer">
          <div class="actions">
            <lib-button
              variant="secondary"
              type="button"
              [disabled]="loading()"
              (clicked)="onCancel()"
            >
              {{ data.cancelButtonText || 'Cancelar' }}
            </lib-button>
            <lib-button
              variant="primary"
              type="button"
              [disabled]="loading() || !isValid()"
              (clicked)="onConfirm()"
            >
              {{ loading() ? 'Procesando...' : data.confirmButtonText || 'Aceptar' }}
            </lib-button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
      }

      .modal-container {
        display: flex;
        flex-direction: column;
        background: var(--md-sys-color-surface, white);
        border-radius: var(--lib-border-radius, 8px);
        overflow: hidden;
        color: var(--md-sys-color-on-surface, #0d1b2a);
        min-width: 500px;
        max-width: 600px;
        box-shadow:
          0 4px 6px -1px rgba(0, 0, 0, 0.1),
          0 2px 4px -1px rgba(0, 0, 0, 0.06);
      }

      .dialog-header {
        background-color: var(--md-sys-color-primary, #1a5eaf);
        padding: 0.75rem 1.5rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      }

      .dialog-header-primary {
        background-color: var(--md-sys-color-primary, #1a5eaf);
      }

      .dialog-header-warning {
        background-color: var(--md-sys-color-error, #dc2626);
      }

      .dialog-title {
        color: var(--md-sys-color-on-primary, white);
        margin: 0;
        font-size: 1.1rem;
        font-weight: 600;
      }

      .close-btn {
        background: none;
        border: none;
        color: var(--md-sys-color-on-primary, white);
        font-size: 1.1rem;
        cursor: pointer;
        padding: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: background-color 0.2s;
      }

      .close-btn:hover:not(:disabled) {
        background-color: rgba(255, 255, 255, 0.1);
      }

      .close-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .dialog-content {
        padding: 1rem 1.5rem;
        max-height: 60vh;
        overflow-y: auto;
      }

      .modal-description {
        margin: 0 0 1rem 0;
        font-size: 0.875rem;
        color: var(--md-sys-color-on-surface-variant, #5f5f6b);
      }

      .textarea-wrapper {
        margin-top: 0.5rem;
      }

      .dialog-footer {
        display: flex;
        justify-content: flex-end;
        padding: 0.75rem 1.5rem;
        border-top: 1px solid var(--md-sys-color-outline-variant, #e5e7eb);
        background-color: var(--md-sys-color-surface-container, #f9fafb);
      }

      .actions {
        display: flex;
        gap: 0.75rem;
      }

      .dialog-content::-webkit-scrollbar {
        width: 8px;
      }

      .dialog-content::-webkit-scrollbar-track {
        background: var(--md-sys-color-surface-container-low, #f1f1f1);
        border-radius: 4px;
      }

      .dialog-content::-webkit-scrollbar-thumb {
        background: var(--md-sys-color-outline, #c1c1c1);
        border-radius: 4px;
      }

      .dialog-content::-webkit-scrollbar-thumb:hover {
        background: var(--md-sys-color-outline-variant, #a8a8a8);
      }

      .modal-container {
        animation: fadeIn 0.2s ease-out;
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @media (prefers-color-scheme: dark) {
        .dialog-footer {
          background-color: var(--md-sys-color-surface-container, #2d2d51);
          border-top-color: var(--md-sys-color-outline-variant, #3c4654);
        }

        .dialog-content {
          background-color: var(--md-sys-color-surface, #1a1a2e);
        }

        .modal-description {
          color: var(--md-sys-color-on-surface-variant, #9e9eb0);
        }

        .dialog-content::-webkit-scrollbar-track {
          background: var(--md-sys-color-surface-container, #2d2d51);
        }

        .dialog-content::-webkit-scrollbar-thumb {
          background: var(--md-sys-color-outline, #5a5a7a);
        }
      }
    `,
  ],
})
export class ConfirmReasonModalComponent {
  @Input() data: ConfirmReasonData = {
    title: 'Confirmar',
    minLength: 2,
    maxLength: 300,
  };
  @Output() confirm = new EventEmitter<ConfirmReasonResult>();
  @Output() cancelClick = new EventEmitter<void>();

  reason = model('');
  loading = signal(false);
  touched = signal(false);

  private getMinLength(): number {
    return this.data.minLength ?? 2;
  }

  private getMaxLength(): number {
    return this.data.maxLength ?? 300;
  }

  getErrorMessage(): string {
    if (!this.touched()) return '';

    const motivo = this.reason().trim();
    const minLength = this.getMinLength();
    const maxLength = this.getMaxLength();

    if (motivo.length === 0) {
      return 'El motivo es requerido';
    }

    if (motivo.length > 0 && motivo.length < minLength) {
      return `El motivo debe tener al menos ${minLength} caracteres`;
    }

    if (motivo.length > maxLength) {
      return `El motivo no puede exceder los ${maxLength} caracteres`;
    }

    return '';
  }

  isValid(): boolean {
    const motivo = this.reason().trim();
    const minLength = this.getMinLength();
    const maxLength = this.getMaxLength();

    if (motivo.length < minLength) {
      return false;
    }

    if (motivo.length > maxLength) {
      return false;
    }

    return true;
  }

  onBlur(): void {
    this.touched.set(true);
  }

  onInput(): void {
    if (this.isValid()) {
      this.touched.set(false);
    }
  }

  onConfirm(): void {
    if (!this.isValid()) {
      this.touched.set(true);
      return;
    }

    this.loading.set(true);

    setTimeout(() => {
      this.confirm.emit({
        confirmed: true,
        reason: this.reason().trim(),
      });
      this.loading.set(false);
    }, 100);
  }

  onCancel(): void {
    this.cancelClick.emit();
  }

  onOverlayClick(event: MouseEvent | KeyboardEvent | Event): void {
    if (event instanceof KeyboardEvent) {
      if (event.key === ' ' || event.key === 'Space') {
        event.preventDefault();
      }
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Space') {
        if (event.target === event.currentTarget) {
          this.onCancel();
        }
      }
      return;
    }

    if (event instanceof MouseEvent && event.target === event.currentTarget) {
      this.onCancel();
    }
  }
}
